/**
 * MiraFeedbackWidget — floating bottom-right pill + chat drawer.
 *
 * Voice / brand:
 *   - Alliance Navy as the resting pill colour; Alliance Steel on hover.
 *   - Editorial, indie-magazine tone in the placeholder + empty-state.
 *   - Lowercase em-dash "or something else…" as the escape-hatch chip.
 *   - One sentence per turn from Mira whenever possible.
 *
 * Surface contract:
 *   - Mounts via <MiraFeedbackWidget /> at the App root.
 *   - Visible only when `useAuth().user` is truthy (gated below).
 *   - Reads CSS vars `--alliance-navy` / `--alliance-steel` so theme
 *     mode (light/dark) propagates automatically.
 *   - Lazy-loads html2canvas at the moment Mira asks for a screenshot,
 *     so first paint stays tiny (~1.4 MB saved).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Camera, Check, Loader2, Inbox, ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useMira } from "../contexts/MiraContext";
import { readCsrfToken } from "../lib/csrf";
import { collectTelemetry, getConsoleSnapshot } from "../lib/mira-telemetry";

// The MARFI logo lives in /public so CRA serves it verbatim — no
// build-time import bloat, and a single rounded-full wrapper does the
// circle crop without a sharper image-mask SVG.
const MARFI_LOGO_SRC = "/mira/marfi-logo.jpeg";

type MiraAction = "continue" | "capture_screen" | "ready_to_submit";

type ChatTurn = {
  role: "user" | "mira";
  text: string;
  // Quick-replies are surfaced on Mira's most recent turn only.
  quick_replies?: string[];
  action?: MiraAction;
};

type MyRequestItem = {
  session_id: string;
  issue_number: number;
  issue_url: string;
  wiki_url: string;
  title: string;
  kind: string;
  severity: string;
  state: "open" | "resolved";
  submitted_at: string;
};

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/**
 * Wrapper around axios.post that attaches the CSRF double-submit
 * header. The backend CSRFMiddleware rejects any cookie-authenticated
 * non-GET request that's missing the `X-CSRF-Token` header. The shared
 * `api` client in src/lib/api.ts already injects this via interceptor,
 * but Mira keeps its own thin axios call so this widget can be lifted
 * into other apps without dragging the WMT api module along.
 */
async function miraPost<T>(path: string, body: unknown): Promise<T> {
  const token = readCsrfToken();
  const headers: Record<string, string> = {};
  if (token) headers["X-CSRF-Token"] = token;
  const bearer = localStorage.getItem("access_token");
  if (bearer) headers["Authorization"] = `Bearer ${bearer}`;
  const { data } = await axios.post<T>(`${API_BASE}${path}`, body, {
    withCredentials: true,
    timeout: 45_000,
    headers,
  });
  return data;
}

const greetingTurn: ChatTurn = {
  role: "mira",
  text:
    "Hi — I'm Mira. Spotted a bug, dreamt up a feature, or just stuck on something? Tell me in a sentence or two and I'll get it to the team.",
  quick_replies: [
    "something broke",
    "an idea for the app",
    "I'm stuck",
    "or something else…",
  ],
  action: "continue",
};

/**
 * LocalStorage key for the in-flight conversation. Per-user so a
 * different account on the same browser never inherits prior turns,
 * even if cleanup somehow doesn't fire on logout (defense in depth).
 * `_v1_` suffix lets future format changes invalidate old payloads
 * without a migration step.
 */
const STORAGE_KEY_PREFIX = "mira_conv_v1_";

type PersistedConv = {
  sessionId: string | null;
  turns: ChatTurn[];
  view: "chat" | "list";
  screenshotAttached: boolean;
  submitted: { issue_url: string; issue_number: number } | null;
};

function storageKeyFor(userId?: string | null): string | null {
  if (!userId) return null;
  return STORAGE_KEY_PREFIX + userId;
}

function readStored(userId?: string | null): PersistedConv | null {
  const key = storageKeyFor(userId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedConv;
    // Defensive — make sure we got something widget-shaped before
    // rehydrating, otherwise reset to defaults.
    if (!parsed || typeof parsed !== "object") return null;
    if (!Array.isArray(parsed.turns)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(userId: string | null | undefined, conv: PersistedConv): void {
  const key = storageKeyFor(userId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(conv));
  } catch {
    /* localStorage full / blocked — degrade silently */
  }
}

function clearStored(userId: string | null | undefined): void {
  const key = storageKeyFor(userId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

const MiraFeedbackWidget: React.FC = () => {
  const { user } = useAuth();
  // Track the previous user so we can scrub their localStorage entry
  // on logout, even if they never explicitly hit "start over".
  const prevUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    const curr = (user?.id as string | undefined) ?? null;
    if (!curr && prevUserIdRef.current) {
      clearStored(prevUserIdRef.current);
    }
    prevUserIdRef.current = curr;
  }, [user?.id]);

  // Bail BEFORE any further hooks so the hook order stays stable across
  // renders (otherwise React throws "Rendered more hooks than during
  // the previous render"). The pill is signed-in-only — return null
  // for unauthenticated visitors.
  if (!user) return null;
  return <MiraFeedbackWidgetInner userId={(user.id as string) ?? null} />;
};

const MiraFeedbackWidgetInner: React.FC<{ userId: string | null }> = ({ userId }) => {
  // Rehydrate any prior conversation from localStorage on first mount.
  // The lazy initializer pattern means we read storage exactly once
  // per (userId) — subsequent renders use the React state directly.
  // Drawer "open" is intentionally NOT persisted: a fresh tab should
  // start with the widget closed, but reopen to the same conversation.
  const initial = useMemo<PersistedConv>(
    () => readStored(userId) ?? {
      sessionId: null,
      turns: [greetingTurn],
      view: "chat",
      screenshotAttached: false,
      submitted: null,
    },
    // userId is stable per-render of this inner component because the
    // outer wrapper unmounts/remounts this inner on user change.
    // Intentional read-once init for that reason.
    [],
  );

  // Two views: "chat" is the default Mira dialog; "list" shows the
  // user's prior submissions with their open/resolved state.
  const [view, setView] = useState<"chat" | "list">(initial.view);
  // Open/close lives in shared context so the navbar trigger button
  // (in Layout.tsx) and the drawer (here) coordinate without prop drilling.
  const { open, setOpen } = useMira();
  const [sessionId, setSessionId] = useState<string | null>(initial.sessionId);
  const [turns, setTurns] = useState<ChatTurn[]>(
    initial.turns.length > 0 ? initial.turns : [greetingTurn],
  );
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [screenshotting, setScreenshotting] = useState(false);
  const [screenshotAttached, setScreenshotAttached] = useState(initial.screenshotAttached);
  const [submitted, setSubmitted] = useState<{ issue_url: string; issue_number: number } | null>(
    initial.submitted,
  );
  // My-requests list state.
  const [myRequests, setMyRequests] = useState<MyRequestItem[] | null>(null);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Persist on any meaningful state change. We deliberately exclude
  // ``open`` so closing the drawer doesn't trigger a write and the
  // next session reopens to chat naturally. ``submitted`` IS persisted
  // so the confirmation card survives a reload until the user hits
  // "start over".
  useEffect(() => {
    if (!userId) return;
    writeStored(userId, {
      sessionId,
      turns,
      view,
      screenshotAttached,
      submitted,
    });
  }, [userId, sessionId, turns, view, screenshotAttached, submitted]);

  // Auto-scroll to the latest turn whenever the transcript grows.
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, open, submitting]);

  const lastMira = useMemo(() => {
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].role === "mira") return turns[i];
    }
    return null;
  }, [turns]);

  const reset = () => {
    setSessionId(null);
    setTurns([greetingTurn]);
    setInput("");
    setScreenshotAttached(false);
    setSubmitted(null);
    // Wipe persisted state too — "start over" means start over,
    // including for the next page reload.
    clearStored(userId);
  };

  const sendToMira = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || sending) return;
      setSending(true);
      setTurns((prev) => [...prev, { role: "user", text: trimmed }]);
      setInput("");
      try {
        // Attach a telemetry snapshot on the FIRST turn (when we don't
        // yet have a session id). Sending it later would just bloat
        // every chat round-trip — the snapshot rarely changes
        // meaningfully mid-session, and we re-snapshot at /submit
        // anyway so the final report reflects the page they were
        // actually on when they hit Send.
        const payload: {
          session_id: string | null;
          message: string;
          telemetry?: ReturnType<typeof collectTelemetry>;
        } = { session_id: sessionId, message: trimmed };
        if (!sessionId) {
          try { payload.telemetry = collectTelemetry(); } catch { /* noop */ }
        }
        const data = await miraPost<{
          session_id: string;
          message: string;
          quick_replies: string[];
          action: MiraAction;
        }>("/api/feedback/chat", payload);
        setSessionId(data.session_id);
        setTurns((prev) => [
          ...prev,
          {
            role: "mira",
            text: data.message,
            quick_replies: data.quick_replies,
            action: data.action,
          },
        ]);
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          "Mira couldn't reach the team. Try again in a moment.";
        toast.error(msg);
        // Roll back the user's optimistic turn so they can edit + retry.
        setTurns((prev) => prev.slice(0, -1));
        setInput(trimmed);
      } finally {
        setSending(false);
      }
    },
    [sending, sessionId]
  );

  const attachScreenshot = useCallback(async () => {
    if (!sessionId || screenshotting) return;
    setScreenshotting(true);
    try {
      // Hide the drawer briefly so it's not in the capture.
      setOpen(false);
      // Lazy-load html2canvas — saves ~1.4 MB on first paint for users
      // who never open the widget.
      const { default: html2canvas } = await import("html2canvas");
      await new Promise((r) => setTimeout(r, 250));
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: Math.min(1, window.devicePixelRatio || 1),
        logging: false,
        backgroundColor: "#ffffff",
      });
      const dataUrl = canvas.toDataURL("image/png");
      setOpen(true);
      await miraPost("/api/feedback/capture-screen", {
        session_id: sessionId,
        image_data_url: dataUrl,
        page_url: window.location.pathname + window.location.search,
      });
      setScreenshotAttached(true);
      setTurns((prev) => [
        ...prev,
        {
          role: "mira",
          text: "Got it — screenshot attached. Anything else worth flagging, or shall I send this off?",
          quick_replies: ["send it off", "one more thing", "or something else…"],
          action: "ready_to_submit",
        },
      ]);
    } catch (err) {
      setOpen(true);
      toast.error("Couldn't grab the screenshot. You can send the report without it.");
    } finally {
      setScreenshotting(false);
    }
  }, [sessionId, screenshotting]);

  const submitToTeam = useCallback(async () => {
    if (!sessionId || submitting) return;
    setSubmitting(true);
    try {
      // Final telemetry snapshot + the console log buffer collected
      // since app boot. These ride along with the submission so the
      // GitHub issue body lands with full diagnostic context — the
      // engineering team doesn't have to play "what browser was this"
      // ping-pong with the reporter.
      let telemetry: ReturnType<typeof collectTelemetry> | undefined;
      let console_logs: ReturnType<typeof getConsoleSnapshot> | undefined;
      try { telemetry = collectTelemetry(); } catch { /* noop */ }
      try { console_logs = getConsoleSnapshot(); } catch { /* noop */ }
      const data = await miraPost<{ issue_url: string; issue_number: number }>(
        "/api/feedback/submit",
        { session_id: sessionId, telemetry, console_logs }
      );
      setSubmitted({ issue_url: data.issue_url, issue_number: data.issue_number });
      toast.success(`Sent to the team — issue #${data.issue_number}`);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Submission failed. Try again in a moment.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, submitting]);

  const loadMyRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const csrf = readCsrfToken();
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (csrf) headers["X-CSRF-Token"] = csrf;
      const { data } = await axios.get<MyRequestItem[]>(
        `${API_BASE}/api/feedback/my-requests`,
        { withCredentials: true, headers, timeout: 20_000 }
      );
      setMyRequests(data);
    } catch (err) {
      toast.error("Couldn't load your requests.");
      setMyRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  // Lazy-fetch the user's request list when they switch to the list view.
  useEffect(() => {
    if (view === "list" && open && myRequests === null && !requestsLoading) {
      loadMyRequests();
    }
  }, [view, open, myRequests, requestsLoading, loadMyRequests]);

  const handleQuickReply = (chip: string) => {
    if (chip === "or something else…") {
      // Just focus the input — let the user write freely.
      const el = document.querySelector<HTMLInputElement>(
        '[data-testid="mira-input"]'
      );
      el?.focus();
      return;
    }
    // Every other chip is treated as a regular user message. Submission
    // is intentionally driven by the structured `mira-send-to-team`
    // button only — the previous heuristic that matched chip text
    // containing "send" would silently break if the LLM rephrased the
    // chip, leading to "I clicked send and nothing happened" bug
    // reports.
    sendToMira(chip);
  };

  return (
    <>
      {/* The trigger pill is no longer rendered here — it now lives in
          the top navbar (see <MiraNavTrigger /> rendered from
          Layout.tsx). The drawer below mounts at the App root so route
          changes don't unmount it. */}

      {/* Drawer — bottom-right anchored. Doesn't dim the page; this is
          a side-companion, not a modal interruption. */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mira-drawer"
            data-testid="mira-feedback-drawer"
            initial={{ opacity: 0, y: 32, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 32, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            className="fixed z-[60] bottom-6 right-6 flex flex-col rounded-2xl overflow-hidden border"
            style={{
              width: "min(28rem, calc(100vw - 2rem))",
              height: "min(34rem, calc(100vh - 6rem))",
              backgroundColor: "var(--bg-primary, #ffffff)",
              borderColor: "var(--border-default, #e5e7eb)",
              boxShadow: "0 30px 60px -20px rgba(11, 46, 78, 0.35)",
              color: "var(--text-primary, #111827)",
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3 border-b"
              style={{
                backgroundColor: "var(--alliance-navy, #0B2E4E)",
                borderColor: "var(--border-default, #e5e7eb)",
              }}
            >
              <div className="flex items-center gap-2.5 text-white">
                <span
                  className="flex items-center justify-center w-9 h-9 rounded-full overflow-hidden bg-white"
                  style={{ boxShadow: "inset 0 0 0 1.5px rgba(255,255,255,0.9)" }}
                  data-testid="mira-header-logo"
                >
                  <img
                    src={MARFI_LOGO_SRC}
                    alt="MARFI"
                    className="w-9 h-9 object-cover"
                    draggable={false}
                  />
                </span>
                <div className="leading-tight">
                  <p className="text-sm font-bold tracking-wide">MIRA</p>
                  <p className="text-[11px] opacity-80">by MARFI</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {view === "chat" ? (
                  <button
                    onClick={() => setView("list")}
                    aria-label="View my requests"
                    title="My requests"
                    className="text-white/80 hover:text-white transition-colors p-1 rounded-md"
                    data-testid="mira-my-requests-button"
                  >
                    <Inbox size={16} />
                  </button>
                ) : (
                  <button
                    onClick={() => setView("chat")}
                    aria-label="Back to chat"
                    title="Back to chat"
                    className="text-white/80 hover:text-white transition-colors p-1 rounded-md"
                    data-testid="mira-back-to-chat"
                  >
                    <ArrowLeft size={16} />
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close Mira"
                  className="text-white/80 hover:text-white transition-colors p-1 rounded-md"
                  data-testid="mira-close-button"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Transcript — chat view only */}
            {view === "chat" && (
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
              data-testid="mira-transcript"
              style={{ backgroundColor: "var(--bg-secondary, #f9fafb)" }}
            >
              {turns.map((t, i) => (
                <MiraBubble key={i} turn={t} />
              ))}

              {sending && (
                <div className="flex items-center gap-2 text-xs opacity-70">
                  <Loader2 size={14} className="animate-spin" />
                  Mira is thinking…
                </div>
              )}

              {submitted && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl p-3 border text-sm"
                  style={{
                    backgroundColor: "rgba(22, 163, 74, 0.08)",
                    borderColor: "rgba(22, 163, 74, 0.35)",
                    color: "var(--text-primary, #111827)",
                  }}
                  data-testid="mira-submitted-card"
                >
                  <div className="flex items-center gap-2 font-semibold mb-1">
                    <Check size={16} />
                    Sent — issue #{submitted.issue_number}
                  </div>
                  <p className="opacity-80 text-xs leading-relaxed">
                    The team can see this now. You can keep it open if you want to add anything, or start fresh.
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <a
                      href={submitted.issue_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs underline opacity-80 hover:opacity-100"
                      data-testid="mira-issue-link"
                    >
                      view on GitHub →
                    </a>
                    <button
                      onClick={reset}
                      className="ml-auto text-xs font-semibold underline opacity-80 hover:opacity-100"
                      data-testid="mira-start-over"
                    >
                      start over
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
            )}

            {/* Quick-reply chips for the most recent Mira turn — only
                while we're not in the submitted state. */}
            {view === "chat" && lastMira?.quick_replies && !submitted && (
              <div
                className="flex flex-wrap gap-1.5 px-4 py-2 border-t"
                style={{
                  borderColor: "var(--border-default, #e5e7eb)",
                  backgroundColor: "var(--bg-primary, #ffffff)",
                }}
                data-testid="mira-quick-replies"
              >
                {lastMira.quick_replies.map((chip, i) => (
                  <button
                    key={i}
                    onClick={() => handleQuickReply(chip)}
                    disabled={sending || submitting || screenshotting}
                    className="text-xs px-3 py-1.5 rounded-full border transition-colors hover:bg-opacity-5"
                    style={{
                      borderColor: "var(--alliance-steel, #2B5C82)",
                      color: "var(--alliance-steel, #2B5C82)",
                      backgroundColor: "transparent",
                    }}
                    data-testid={`mira-chip-${i}`}
                  >
                    {chip}
                  </button>
                ))}
                {lastMira.action === "capture_screen" && !screenshotAttached && (
                  <button
                    onClick={attachScreenshot}
                    disabled={screenshotting}
                    className="text-xs px-3 py-1.5 rounded-full text-white flex items-center gap-1.5 transition-opacity hover:opacity-90 disabled:opacity-60"
                    style={{ backgroundColor: "var(--alliance-navy, #0B2E4E)" }}
                    data-testid="mira-attach-screenshot"
                  >
                    {screenshotting ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Camera size={12} />
                    )}
                    {screenshotting ? "capturing…" : "grab a screenshot"}
                  </button>
                )}
              </div>
            )}

            {/* Ready-to-submit banner — appears above the composer
                whenever Mira's most recent turn has action="ready_to_submit".
                Driven STRICTLY off the structured action signal, never
                off chip text — that's the GH-Mira-hardening fix to
                avoid silent breakage when the model rephrases chips.
                The composer below stays a normal "keep chatting" form
                so a "one more thing" follow-up still works. */}
            {view === "chat" && !submitted && lastMira?.action === "ready_to_submit" && sessionId && (
              <div
                className="flex items-center gap-2 px-3 py-2 border-t"
                style={{
                  borderColor: "var(--border-default, #e5e7eb)",
                  backgroundColor: "rgba(11,46,78,0.06)",
                }}
              >
                <span className="text-[11px] opacity-70 flex-1">
                  Ready when you are.
                </span>
                <button
                  type="button"
                  onClick={submitToTeam}
                  disabled={submitting}
                  className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60"
                  style={{ backgroundColor: "var(--alliance-navy, #0B2E4E)" }}
                  data-testid="mira-send-to-team"
                >
                  {submitting ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Check size={14} />
                  )}
                  {submitting ? "sending…" : "Send to the team"}
                </button>
              </div>
            )}

            {/* Composer — chat view only */}
            {view === "chat" && !submitted && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendToMira(input);
                }}
                className="flex items-center gap-2 px-3 py-3 border-t"
                style={{
                  borderColor: "var(--border-default, #e5e7eb)",
                  backgroundColor: "var(--bg-primary, #ffffff)",
                }}
              >
                <input
                  data-testid="mira-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={sending || submitting}
                  placeholder={
                    sessionId
                      ? "keep it casual — what happened?"
                      : "tell Mira what's on your mind…"
                  }
                  className="flex-1 text-sm px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-shadow"
                  style={{
                    borderColor: "var(--border-default, #e5e7eb)",
                    backgroundColor: "var(--bg-secondary, #f9fafb)",
                    color: "var(--text-primary, #111827)",
                  }}
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  className="px-3 py-2 rounded-lg text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60"
                  style={{ backgroundColor: "var(--alliance-navy, #0B2E4E)" }}
                  data-testid="mira-send-button"
                  aria-label="Send message"
                >
                  <Send size={14} />
                </button>
              </form>
            )}

            {/* My Requests list view — appears when the user toggles
                the inbox icon in the header. Shows their prior Mira
                submissions with open/resolved status and a deep-link
                to the in-app wiki page for each. */}
            {view === "list" && (
              <div
                className="flex-1 overflow-y-auto px-3 py-3 space-y-2"
                data-testid="mira-my-requests-list"
                style={{ backgroundColor: "var(--bg-secondary, #f9fafb)" }}
              >
                <div className="flex items-center justify-between px-1 mb-1">
                  <p className="text-xs font-bold uppercase tracking-widest opacity-70">
                    Your requests
                  </p>
                  <button
                    onClick={loadMyRequests}
                    disabled={requestsLoading}
                    className="text-[11px] underline opacity-70 hover:opacity-100"
                    data-testid="mira-refresh-requests"
                  >
                    refresh
                  </button>
                </div>

                {requestsLoading && myRequests === null && (
                  <div className="flex items-center gap-2 text-xs opacity-70 px-1">
                    <Loader2 size={12} className="animate-spin" /> loading…
                  </div>
                )}

                {myRequests && myRequests.length === 0 && (
                  <div className="text-xs opacity-70 italic px-1 py-3">
                    Nothing here yet. Submit something via Mira and it'll appear here once the team has it.
                  </div>
                )}

                {myRequests?.map((r) => (
                  <a
                    key={r.session_id}
                    href={r.wiki_url}
                    onClick={(e) => {
                      // Use SPA navigation when the wiki URL is on the
                      // same origin — keeps the in-app state intact.
                      try {
                        const u = new URL(r.wiki_url);
                        if (u.origin === window.location.origin) {
                          e.preventDefault();
                          window.location.assign(u.pathname);
                        }
                      } catch {
                        /* external URL — let the browser handle it */
                      }
                    }}
                    className="block rounded-xl p-3 border text-sm transition-shadow hover:shadow-md"
                    style={{
                      borderColor: "var(--border-default, #e5e7eb)",
                      backgroundColor: "var(--bg-primary, #ffffff)",
                      color: "var(--text-primary, #111827)",
                    }}
                    data-testid={`mira-request-card-${r.session_id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize"
                        style={{
                          backgroundColor:
                            r.state === "resolved"
                              ? "rgba(22,163,74,0.12)"
                              : "rgba(37,99,235,0.12)",
                          color:
                            r.state === "resolved" ? "#16A34A" : "#2563EB",
                        }}
                        data-testid={`mira-request-state-${r.session_id}`}
                      >
                        {r.state}
                      </span>
                      <span className="text-[11px] opacity-70 capitalize">
                        {r.kind} · {r.severity}
                      </span>
                      <span className="text-[11px] opacity-50 ml-auto">
                        #{r.issue_number}
                      </span>
                    </div>
                    <div className="font-semibold leading-snug">
                      {r.title}
                    </div>
                    <div className="flex items-center gap-1 text-[11px] mt-1 opacity-70">
                      open in wiki <ExternalLink size={11} />
                    </div>
                  </a>
                ))}
              </div>
            )}

            {/* Always-on footer attribution. Sits below both the
                composer and the submitted-card states so MARFI is
                visible regardless of where the user is in the flow. */}
            <a
              href="https://marfi.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-center py-2 border-t tracking-wide hover:underline transition-opacity opacity-70 hover:opacity-100"
              style={{
                borderColor: "var(--border-default, #e5e7eb)",
                backgroundColor: "var(--bg-primary, #ffffff)",
                color: "var(--text-muted, #6b7280)",
              }}
              data-testid="mira-marfi-link"
            >
              powered by <span className="font-semibold">MARFI</span> · marfi.ai
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const MiraBubble: React.FC<{ turn: ChatTurn }> = ({ turn }) => {
  const isMira = turn.role === "mira";
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex ${isMira ? "justify-start" : "justify-end"}`}
    >
      <div
        className="max-w-[85%] text-sm leading-relaxed px-3 py-2 rounded-2xl"
        style={
          isMira
            ? {
                backgroundColor: "var(--bg-primary, #ffffff)",
                color: "var(--text-primary, #111827)",
                border: "1px solid var(--border-default, #e5e7eb)",
                borderTopLeftRadius: "0.25rem",
              }
            : {
                backgroundColor: "var(--alliance-navy, #0B2E4E)",
                color: "#ffffff",
                borderTopRightRadius: "0.25rem",
              }
        }
        data-testid={isMira ? "mira-bubble-assistant" : "mira-bubble-user"}
      >
        {turn.text}
      </div>
    </motion.div>
  );
};

export default MiraFeedbackWidget;

/**
 * MiraNavTrigger — small circular MARFI-logo button intended for the
 * top navbar (Layout.tsx). Auth-gated like the drawer; signs out
 * cleanly when the user logs out. Pairs with <MiraFeedbackWidget />
 * mounted at the App root via shared `MiraContext` state.
 */
export const MiraNavTrigger: React.FC = () => {
  const { user } = useAuth();
  const { open, setOpen } = useMira();
  if (!user) return null;
  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      aria-label="Talk to Mira"
      title="Talk to Mira"
      data-testid="mira-feedback-pill"
      className="relative flex items-center justify-center w-9 h-9 rounded-full transition-all hover:scale-105 active:scale-95"
      style={{
        backgroundColor: open
          ? "var(--alliance-navy, #0B2E4E)"
          : "transparent",
        boxShadow: open
          ? "0 0 0 2px var(--alliance-navy, #0B2E4E)"
          : "inset 0 0 0 1px var(--border-default, #e5e7eb)",
      }}
    >
      <span
        className="flex items-center justify-center w-7 h-7 rounded-full overflow-hidden bg-white"
        style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }}
      >
        <img
          src={MARFI_LOGO_SRC}
          alt=""
          className="w-7 h-7 object-cover"
          draggable={false}
        />
      </span>
    </button>
  );
};

